// DraftItemPhotos — SIMPLE multi-photo strip for one draft position
// (#1451). Renders existing-photo thumbnails (each with a remove «×»)
// in upload order, then an «add» tile while the item holds fewer than
// `REQUEST_ITEM_PHOTOS_MAX` photos. Clicking any thumbnail opens the
// shared `PhotoGallery` over ALL of the item's photos.
//
// @aiPurpose Multi-photo editor affordance for SIMPLE positions; lives
//   here (not in `DraftItemEditRow`, which is already near the file-size
//   cap) so the strip + gallery trigger stay cohesive.
// @aiInvariant ONE FILE PER ADD ACTION (D-B3): the file input is NOT
//   `multiple`, and the add tile is disabled while an upload is in
//   flight for this item OR the item is at `REQUEST_ITEM_PHOTOS_MAX`.
// @aiInvariant Session blob previews live in the draft-scoped
//   `DraftPhotoPreviews` registry (keyed by object key) so a duplicated
//   position can read its source's preview by key (#1493). This strip
//   only registers a blob on a successful add; the registry owns revoke
//   + unmount cleanup. Removing a key only drops it from the item's
//   `photos`; the existing discard / save-commit cleanup deletes any
//   orphaned storage object — no eager storage-delete here.

import { type ClipboardEvent as ReactClipboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { REQUEST_ITEM_PHOTOS_MAX } from '@magimp/shared';
import { Button, Icon, IconButton } from '../../../ui/index.js';
import {
  extractFromClipboardApi,
  extractFromClipboardEvent,
  type ClipboardExtractError,
} from '../clipboardExtract.js';
import { PhotoGallery } from './PhotoGallery.js';
import { useDraftPhotoPreviews } from './DraftPhotoPreviews.js';

const ISSUED_PHOTO_KEY_RE = /^request-item-photo\/[0-9a-f-]{36}\/[0-9a-f-]{36}$/i;

function isPersistedItemId(id: string): boolean {
  return /^it-\d{4}$/.test(id);
}

export interface DraftItemPhotosProps {
  itemId: string;
  photos: string[];
  /** Persisted request id pinned after the draft's first save. */
  persistedRequestId: string | null;
  uploading: boolean;
  uploadError: string | null;
  frozen: boolean;
  onAddPhoto: (file: File) => Promise<{ objectKey: string } | null>;
  onRemovePhoto: (key: string) => void;
}

export function DraftItemPhotos({
  itemId,
  photos,
  persistedRequestId,
  uploading,
  uploadError,
  frozen,
  onAddPhoto,
  onRemovePhoto,
}: DraftItemPhotosProps) {
  const { t } = useTranslation(['requests', 'common']);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [galleryStart, setGalleryStart] = useState<number | null>(null);
  const [clipboardError, setClipboardError] = useState<ClipboardExtractError | null>(null);
  // Session blob previews live in the draft-scoped registry, keyed by
  // object key, so a cloned position can read this preview by key (#1493).
  const { getPreviewUrl, registerBlob, registerAuthorizer, requestKey } = useDraftPhotoPreviews();

  // Guards the post-await registerBlob: the SIMPLE delete control stays
  // enabled during an upload, so the row can unmount while onAddPhoto is
  // in flight; registering after the reconciler dropped the (now removed)
  // key would leak an orphan blob URL until the draft closes, breaking the
  // reference-counted revoke guarantee (#1493 BR-5). The setup MUST reset
  // the ref to true: React StrictMode (dev) runs setup -> cleanup -> setup
  // while preserving the ref, so a cleanup-only guard would latch false on
  // a still-mounted strip and skip every legitimate registration.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Resolve saved-photo signed-download URLs through the registry instead
  // of a per-thumbnail effect (#1494): a persisted row registers itself as
  // the authorizer for each issued key and asks the registry to fetch it,
  // so a draft-it-N clone that copied the key reads the cached URL by key.
  // Idempotent + at-most-one-fetch guarantees live in the registry.
  useEffect(() => {
    if (persistedRequestId === null || !isPersistedItemId(itemId)) return;
    for (const key of photos) {
      if (ISSUED_PHOTO_KEY_RE.test(key)) {
        registerAuthorizer(key, persistedRequestId, itemId);
        requestKey(key);
      }
    }
  }, [photos, persistedRequestId, itemId, registerAuthorizer, requestKey]);

  const atMax = photos.length >= REQUEST_ITEM_PHOTOS_MAX;
  const addDisabled = uploading || frozen || atMax;

  // Register the blob preview against the upload RESULT key, not FIFO
  // inference: a failed/no-op add resolves `null`, so nothing is
  // registered and no stale URL can be mis-bound to a later key (#1451).
  async function onPickFile(file: File): Promise<void> {
    // A successful add or any new file selection clears the clipboard
    // error so a stale inline message does not linger (FR-5).
    setClipboardError(null);
    const result = await onAddPhoto(file);
    if (result && mountedRef.current) registerBlob(result.objectKey, file);
  }

  // Build the gallery's local-URL map from the registry: only keys that
  // currently hold a blob URL appear, so saved-only keys fall through to
  // the signed-download path inside the gallery.
  const galleryLocalUrls: Record<string, string> = {};
  for (const key of photos) {
    const url = getPreviewUrl(key);
    if (url !== null) galleryLocalUrls[key] = url;
  }

  // Explicit "paste from clipboard" button: read the Async Clipboard API
  // and route the first accepted raster image through the same add path.
  async function onClipboardButtonClick(): Promise<void> {
    if (addDisabled) return;
    const res = await extractFromClipboardApi();
    if (res.ok) void onPickFile(res.file);
    else setClipboardError(res.error);
  }

  // Scoped Ctrl+V on the clipboard button's wrapper: only the button's
  // own `<span onPaste>` handles this, so sibling inputs keep their paste.
  function onClipboardPaste(e: ReactClipboardEvent<HTMLElement>): void {
    if (addDisabled) return;
    const res = extractFromClipboardEvent(e.clipboardData);
    if (res.ok) {
      e.preventDefault();
      void onPickFile(res.file);
    } else {
      setClipboardError(res.error);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void onPickFile(file);
          e.target.value = '';
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {photos.map((key, idx) => (
          <PhotoThumb
            key={key}
            displayUrl={getPreviewUrl(key)}
            onOpen={() => setGalleryStart(idx)}
            onRemove={() => onRemovePhoto(key)}
            removeDisabled={frozen}
          />
        ))}
        {!atMax && (
          <button
            type="button"
            aria-label={t('requests:draft.photoAddMore')}
            disabled={addDisabled}
            onClick={() => fileInputRef.current?.click()}
            style={{
              width: 72,
              height: 54,
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              borderRadius: 'var(--r-1)',
              border: '1px dashed var(--border-2)',
              background: 'var(--bg-2)',
              color: addDisabled ? 'var(--fg-4)' : 'var(--fg-3)',
              cursor: addDisabled ? 'not-allowed' : 'pointer',
              opacity: addDisabled ? 0.6 : 1,
              padding: 0,
            }}
          >
            <Icon name={uploading ? 'image' : 'image-plus'} size={18} />
          </button>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Button
          kind="ghost"
          size="sm"
          icon="upload-cloud"
          disabled={addDisabled}
          onClick={() => fileInputRef.current?.click()}
        >
          {t('requests:draft.photoUpload')}
        </Button>
        <span style={{ display: 'inline-flex' }} onPaste={onClipboardPaste}>
          <Button
            kind="ghost"
            size="sm"
            icon="clipboard-paste"
            disabled={addDisabled}
            aria-label={t('requests:draft.photoFromClipboard')}
            onClick={() => void onClipboardButtonClick()}
          >
            {t('requests:draft.photoFromClipboard')}
          </Button>
        </span>
      </div>
      {clipboardError && (
        <span
          role="alert"
          style={{
            fontSize: 11,
            color: 'var(--status-error-fg)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Icon name="alert-triangle" size={12} />
          {t(`requests:draft.photoClipboard.${clipboardError}`)}
        </span>
      )}
      {uploadError && (
        <span
          style={{
            fontSize: 11,
            color: 'var(--status-error-fg)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Icon name="alert-triangle" size={12} />
          {uploadError}
        </span>
      )}
      <PhotoGallery
        open={galleryStart !== null}
        onClose={() => setGalleryStart(null)}
        keys={photos}
        startIndex={galleryStart ?? 0}
        // The registry owns saved-photo fetches for the draft editor and feeds
        // them in via localUrls; null requestId keeps PhotoGallery from issuing
        // its own duplicate signed-download POST for a key still resolving (#1494).
        requestId={null}
        itemId={itemId}
        localUrls={galleryLocalUrls}
      />
    </div>
  );
}

function PhotoThumb({
  displayUrl,
  onOpen,
  onRemove,
  removeDisabled,
}: {
  displayUrl: string | null;
  onOpen: () => void;
  onRemove: () => void;
  removeDisabled: boolean;
}) {
  const { t } = useTranslation(['requests']);
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        aria-label={t('requests:table.photos')}
        onClick={onOpen}
        style={{
          width: 72,
          height: 54,
          borderRadius: 'var(--r-1)',
          overflow: 'hidden',
          border: '1px solid var(--border-2)',
          background: 'var(--bg-2)',
          padding: 0,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--fg-4)',
        }}
      >
        {displayUrl ? (
          <img
            src={displayUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Icon name="image" size={18} />
        )}
      </button>
      <IconButton
        icon="x"
        size={20}
        label={t('requests:draft.photoRemove')}
        onClick={onRemove}
        disabled={removeDisabled}
        style={{
          position: 'absolute',
          top: -8,
          right: -8,
          background: 'var(--bg-1)',
          border: '1px solid var(--border-2)',
          borderRadius: '50%',
        }}
      />
    </span>
  );
}
